import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore'; 
import { db } from '../firebase'; 
import { useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const ProductPricingTable = ({ carPartList  }) => {
  const { customer, document } = useParams(); 
  const [data, setData] = useState(null);
  const [productTitle, setProductTitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prices, setPrices] = useState(null);
  const [expandedSections, setExpandedSections] = useState({}); 
    const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const docPath = carPartList ? `customers/${customer}/pricingEstimates` : `customers/${customer}/productPricing`
        const docRef = doc(db, docPath, document); 
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const pricingData = docSnap.data()
          if (pricingData.title) {
            setProductTitle(pricingData.title)
          }
          setData(pricingData); 
        } else {
          setError("No such document!");
        }

        if (!carPartList) {
          const productDocPath = `customers/${customer}/products`
          const productDocRef = doc(db, productDocPath, document); 
          const productDocSnap = await getDoc(productDocRef);
  
          if (productDocSnap.exists()) {
            const pricingData = productDocSnap.data()
            if (pricingData.priceOptions) {
              setPrices(pricingData.priceOptions)
              console.log(pricingData.priceOptions)
            }
          } else {
            setError("No such document!");
          }
        }
      } catch (err) {
        setError("Error fetching document: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customer, document]);

  const updateFirebase = async (updatedData) => {
    try {
      const docPath = carPartList ? `customers/${customer}/pricingEstimates` : `customers/${customer}/productPricing`
      const docRef = doc(db, docPath, document); 
      await updateDoc(docRef, updatedData);
      console.log('Document successfully updated in Firestore');
    } catch (error) {
      console.error('Error updating document: ', error);
    }
  };

  const handleExclude = (partIndex, sectionKey, listingIndex) => {
    const updatedData = { ...data };
    const section = carPartList ? updatedData.partPricingEstimates[partIndex].pricing[sectionKey].listings : updatedData[sectionKey] ;

    section.splice(listingIndex, 1);

    setData(updatedData);
    updateFirebase(updatedData);
  };

  const toggleSection = (partIndex, sectionKey) => {
    const sectionIdentifier = `${partIndex}-${sectionKey}`;
    setExpandedSections((prevState) => ({
      ...prevState,
      [sectionIdentifier]: !prevState[sectionIdentifier], 
    }));
  };

  function capitalize(item) {
    return item.charAt(0).toUpperCase() + item.slice(1)
  }
  const calculateStatistics = (listings) => {
    const validPrices = listings
      .map((l) => parseFloat(l.price))
      .filter((p) => !isNaN(p) && p > 0); // ignore non-numbers and zeroes

    if (validPrices.length === 0) return { max: 0, min: 0, average: 0, median: 0 };

    validPrices.sort((a, b) => a - b);
    const max = validPrices[validPrices.length - 1];
    const min = validPrices[0];
    const median = validPrices[Math.floor(validPrices.length / 2)];
    const average = validPrices.reduce((sum, price) => sum + price, 0) / validPrices.length;

    return { max, min, average, median };
  };

  const renderPartListings = (partData, partIndex) => {
    const renderListingsSection = (title, listings, sectionKey) => {
      if (!listings || listings.length === 0) return null;

      const stats = calculateStatistics(listings);

      const sectionIdentifier = `${partIndex}-${sectionKey}`;
      const isExpanded = expandedSections[sectionIdentifier];

      return (
        <div className="pricing-listing-section" key={sectionIdentifier}>
          <h4>eBay {title} Statistics</h4>
          <div className="pricing-statistics">
            <p>Max Price: ${Number.isFinite(stats.max) ? stats.max.toFixed(2) : 'N/A'}</p>
            <p>Min Price: ${Number.isFinite(stats.min) ? stats.min.toFixed(2) : 'N/A'}</p>
            <p>Average Price: ${Number.isFinite(stats.average) ? stats.average.toFixed(2) : 'N/A'}</p>
            <p>Median Price: ${Number.isFinite(stats.median) ? stats.median.toFixed(2) : 'N/A'}</p>
          </div>
          <h4 onClick={() => toggleSection(partIndex, sectionKey)} style={{ cursor: 'pointer' }}>
          eBay {title} Listings ({listings.length} items) {isExpanded ? '▲' : '▼'}
          </h4>
          {isExpanded && (
            <div className="pricing-listings-grid">
              {listings.map((listing, index) => (
                <div key={index} className="pricing-listing">
                  <img src={listing.image} alt={listing.title} />
                  <div className="pricing-listing-info">
                    <h4>
                      <a href={listing.link} target="_blank" rel="noopener noreferrer">
                        {listing.title}
                      </a>
                    </h4>
                    <p>Price: ${listing.price}</p>
                    <p>Condition: {listing.condition}</p>
                    <p>Date: {listing.date}</p>
                    <button onClick={() => handleExclude(partIndex, sectionKey, index)}>
                      Exclude
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    return (
      <div key={partIndex} className="pricing-part-section">
         { partData.part ? ( 
          <>
        <h3>{partData.part}</h3>
        {renderListingsSection('Sold', partData.pricing.sold?.listings, 'sold')}
        {renderListingsSection('Unsold', partData.pricing.unsold?.listings, 'unsold')}
        {renderListingsSection('Active', partData.pricing.active?.listings, 'active')}
        </>
         ) : (
          <>
          <h3>{productTitle}</h3> <br></br>
          {renderListingsSection('Sold', partData.sold?.listings, 'sold')}
          {renderListingsSection('Unsold', partData.unsold?.listings, 'unsold')}
          {renderListingsSection('Active', partData.active?.listings, 'active')}    
          </>
         )}
      </div>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!data ) {
    return <div>No data available</div>;
  }

  return (
    <div>
    <Button 
            variant="outlined" 
            onClick={() => navigate(-1)} // Go back to the previous page
            sx={{  marginTop: 2, marginLeft: 2 }}
          >
            Back
          </Button>
      <div className="pricing-page">
        { carPartList ? 
        (<>{data.partPricingEstimates.map((part, index) => renderPartListings(part, index))}</>) : 
        (<>
        {renderPartListings(data, 0)}
        <p><b>Other Prices Found:</b> 
        {prices && Object.entries(prices).map(([key, value], index) => (
          <>{ value && value !== "$0.00" && (
          <div key={index}>
            <strong>{capitalize(key)}:</strong> {value}
          </div>
          )}</>
  
      ))}
      </p>
      </>)}
    </div>
    </div>
  );
};

export default ProductPricingTable;